import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { HandleAxiosPromise } from "@shared_components/axios/HandleAxiosPromise";
import { FETCH_GEARSTUDIO_DESIGN_USAGE_URL, GearStudioDesign, UsageData } from "@src/types";

type GearStudioDesignContextValue = {
    current_design: GearStudioDesign | null;
    setCurrentDesign: React.Dispatch<React.SetStateAction<GearStudioDesign | null>>;
    globalLoading: boolean;
    setGlobalLoading: React.Dispatch<React.SetStateAction<boolean>>;
    usageData: UsageData;
    fetchUsageData: (user_uuid: string, options?: { showLoading?: boolean }) => void;
    usageDataLoading: boolean;
};

const GearStudioDesignContext = createContext<GearStudioDesignContextValue | undefined>(undefined);

type GearStudioDesignProviderProps = {
    children: React.ReactNode;
};

export const GearStudioDesignProvider = ({
    children,
}: GearStudioDesignProviderProps) => {
    const [current_design, setCurrentDesign] = useState<GearStudioDesign | null>(null);
    const [globalLoading, setGlobalLoading] = useState<boolean>(false); // tracks any loading state to disable buttons on all components
    const [usageData, setUsageData] = useState<UsageData>({ usage_count: 0, usage_reset_time: "", daily_limit: 0 });
    const [usageDataLoading, setUsageDataLoading] = useState<boolean>(false);

    const fetchUsageData = useCallback((user_uuid: string, options?: { showLoading?: boolean }) => {
        if (!user_uuid ) {
            return;
        }
        const shouldShowLoading = options?.showLoading ?? false;
        if (shouldShowLoading) {
            setUsageDataLoading(true);
        }
        console.log("Fetching GearStudio design usage data for user:", user_uuid);
        HandleAxiosPromise({
            method: "get",
            url: FETCH_GEARSTUDIO_DESIGN_USAGE_URL,
            additional_headers: {
                "User-Id": user_uuid,
            },
        }).then((response) => {
            console.log(response)
            if (response && response?.status === 200) {
                setUsageData(response.data as UsageData);
            } else {
                console.error("Unexpected response while fetching usage data:", response);
            }
        }).catch((errorMessage) => {
            console.error("Error fetching GearStudio design usage data:", errorMessage);
        }).finally(() => {
            setUsageDataLoading(false);
        });
    }, []);

    const value = useMemo(() => ({
        current_design,
        setCurrentDesign,
        globalLoading,
        setGlobalLoading,
        usageData,
        fetchUsageData,
        usageDataLoading,
    }), [current_design, globalLoading, usageData, fetchUsageData, usageDataLoading]);



    return (
        <GearStudioDesignContext.Provider value={value}>
            {children}
        </GearStudioDesignContext.Provider>
    );
};

export const useGearStudioDesign = () => {
    const context = useContext(GearStudioDesignContext);
    if (!context) {
        throw new Error("useGearStudioDesign must be used within GearStudioDesignProvider");
    }
    return context;
};
